import { geniFetch } from "./geniBackend";
import type { SupportedImageAspectRatio } from "@/api/images/imageAspectRatio";

export type CatalogTextAction =
  | "title_variants"
  | "description_variants"
  | "another_variant"
  | "deep_strategies"
  | "refine_text"
  | "normalize_user_prompt_ru"
  | "rewrite_user_prompt_en"
  | "generate_image_prompt_from_recommendation";

export type ProductImageEditOrchestrationResult = {
  url: string;
  geniSpent?: number;
  geniBalance?: number;
};

export type ProductImageBillingScope = "standalone_photo" | "product_card";

type ProductImageEditAsyncResponse =
  | {
      jobId: string;
      status: "completed";
      url: string;
      geniSpent?: number;
      geniBalance?: number;
      aiCostUsdTotal?: number;
      aiCostByRequest?: Record<string, number>;
    }
  | { jobId: string; status: "pending" }
  // GC-6: бэк возвращает русский errorText в поле error, errorCode для UX-логики
  // (например, PROHIBITED_CONTENT → подсказка «опишите без конкретного имени»),
  // geniBalance — актуальный баланс после refund (фронт синхронизирует authStore).
  | {
      jobId: string;
      status: "failed";
      error: string;
      errorCode?: string;
      geniBalance?: number;
    }
  | { jobId: string; status: "unknown" };

/**
 * GC-6: типизированная ошибка генерации фото. Бросается из
 * orchestrateProductImageEdit. Содержит errorCode/errorText/geniBalance,
 * чтобы вызывающий код мог:
 *   - показать понятный русский текст пользователю (errorText),
 *   - сделать UX-решение по коду (errorCode),
 *   - обновить локальный баланс geni после refund (geniBalance).
 */
export class ImageEditFailedError extends Error {
  readonly errorCode: string;
  readonly errorText: string;
  readonly geniBalance?: number;

  constructor(input: { errorCode?: string; errorText: string; geniBalance?: number }) {
    super(input.errorText);
    this.name = "ImageEditFailedError";
    this.errorCode = input.errorCode ?? "UNKNOWN";
    this.errorText = input.errorText;
    this.geniBalance = input.geniBalance;
  }
}

// GC-35: бэк отдаёт jobId сразу и продолжает работу в фоне. Polling переживает
// разрыв TCP/прокси — на каждый GET бэк отвечает быстро, тайм-аутов нет.
// 2 c × 150 итераций = 5 минут. Этого хватает на любой Gemini вызов (Gemini
// обычно <30 c, но safety-фильтры/ретраи могут утроить).
const PRODUCT_IMAGE_EDIT_POLL_INTERVAL_MS = 2000;
const PRODUCT_IMAGE_EDIT_POLL_MAX_ATTEMPTS = 150;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function orchestrateProductImageEdit(
  body: {
    sourceImageUrl?: string;
    imageBase64?: string;
    userIntent?: string;
    recommendationContext?: Record<string, unknown>;
    productTitle?: string;
    productDescription?: string;
    aspectRatio?: SupportedImageAspectRatio;
    billingScope?: ProductImageBillingScope;
  },
  idempotencyKey?: string,
): Promise<ProductImageEditOrchestrationResult> {
  if (!idempotencyKey) {
    throw new Error("orchestrateProductImageEdit: idempotencyKey is required");
  }
  const headers: Record<string, string> = { "Idempotency-Key": idempotencyKey };
  const initial = await geniFetch<ProductImageEditAsyncResponse>(
    "/ai/orchestration/product-image/edit",
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    },
  );

  const finalResponse = await waitForProductImageEditResult(initial, idempotencyKey);
  if (finalResponse.status === "failed") {
    // GC-6: пробрасываем структурированную ошибку — компонент чата редактора
    // фото покажет errorText в InlineErrorBanner и подтянет geniBalance.
    throw new ImageEditFailedError({
      errorCode: finalResponse.errorCode,
      errorText: finalResponse.error || "Не удалось сгенерировать изображение. Попробуйте ещё раз.",
      geniBalance: finalResponse.geniBalance,
    });
  }
  if (finalResponse.status === "completed") {
    return {
      url: finalResponse.url,
      geniSpent: finalResponse.geniSpent,
      geniBalance: finalResponse.geniBalance,
    };
  }
  throw new Error("Image generation timed out after 5 minutes");
}

async function waitForProductImageEditResult(
  initial: ProductImageEditAsyncResponse,
  idempotencyKey: string,
): Promise<ProductImageEditAsyncResponse> {
  if (initial.status === "completed" || initial.status === "failed") {
    return initial;
  }
  const pollPath =
    "/ai/orchestration/product-image/edit/result?key=" + encodeURIComponent(idempotencyKey);
  for (let attempt = 0; attempt < PRODUCT_IMAGE_EDIT_POLL_MAX_ATTEMPTS; attempt++) {
    await delay(PRODUCT_IMAGE_EDIT_POLL_INTERVAL_MS);
    const next = await geniFetch<ProductImageEditAsyncResponse>(pollPath, { method: "GET" });
    if (next.status === "completed" || next.status === "failed") return next;
    // 'unknown' значит бэк перезагрузился и потерял in-memory map — спокойно
    // продолжаем поллинг: storeGenerationResult коммитит в БД ДО finally в
    // background-runner, так что финальный статус всё равно подтянется.
  }
  return initial;
}

export async function orchestrateWbCardFixSuggestion(body: {
  errors: string[];
  good: {
    title: string;
    description: string;
    characteristics: Array<{ name: string; value: string }>;
  };
}): Promise<Record<string, unknown>> {
  return geniFetch("/ai/orchestration/wb/card-fix-suggestion", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Исправление тела POST к WB Content API (upload/update) по тексту ошибки API. */
export async function orchestrateWbContentApiBodyFix(body: {
  requestBody: unknown;
  errorText: string;
  additionalErrors?: Record<string, unknown>;
}): Promise<{ requestBody: unknown }> {
  return geniFetch<{ requestBody: unknown }>("/ai/orchestration/wb/content-api-body-fix", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function orchestrateProductIdentity(body: {
  referenceImageUrl: string;
}): Promise<Record<string, unknown>> {
  return geniFetch("/ai/orchestration/recommendations/product-identity", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type NanoBananaSlideOrchestrationResult = {
  imageUrl: string;
  cost: number;
  geniSpent?: number;
  geniBalance?: number;
};

export async function orchestrateNanoBananaSlide(
  body: {
    referenceImageUrl: string;
    creative: Record<string, unknown>;
    productIdentity?: Record<string, unknown> | null;
  },
  idempotencyKey?: string,
): Promise<NanoBananaSlideOrchestrationResult> {
  const headers: Record<string, string> | undefined = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  return geniFetch<NanoBananaSlideOrchestrationResult>(
    "/ai/orchestration/recommendations/nano-banana-slide",
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    },
  );
}

export async function orchestrateCorrectTexts(body: {
  items: Array<{ id: string; text: string; confidence?: number }>;
  expectedLines?: string[];
}): Promise<Array<{ id: string; text: string; correctedText: string; confidence?: number }>> {
  return geniFetch("/ai/orchestration/ocr/correct-texts", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function orchestrateCatalogText(
  action: CatalogTextAction,
  payload: Record<string, unknown>,
): Promise<unknown> {
  return geniFetch("/ai/orchestration/catalog/text", {
    method: "POST",
    body: JSON.stringify({ action, payload }),
  });
}

export async function orchestratePhotoSemanticProfile(body: {
  imageUrl: string;
}): Promise<Record<string, unknown>> {
  return geniFetch("/ai/orchestration/marketplace/photo-semantic-profile", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function orchestrateMarketplaceCompliance(body: {
  imageUrl: string;
  marketplace: string;
  policyJson: string;
}): Promise<{ pass: boolean; violations: string[] }> {
  return geniFetch("/ai/orchestration/marketplace/compliance-check", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function orchestrateCardImagePlan(body: {
  marketplaceDisplayName: string;
  marketplaceKey: string;
  policyJson: string;
  productTitle: string;
  productDescription: string;
  generationNotes: string;
  categoryLabel: string;
  characteristics?: Array<{ name: string; value: string }> | null;
}): Promise<{ images?: unknown[] }> {
  return geniFetch("/ai/orchestration/marketplace/card-image-plan", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
